import type {
  ConversationListItemResponse,
  ConversationPriority,
  ConversationStatus,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import type { ConversationSort } from "@redotech/effect-api-shared/schema/query-options";
import ArrowDown from "@redotech/redo-icons/arrow-down.svg";
import ArrowUp from "@redotech/redo-icons/arrow-up.svg";
import {
  RedoBadge,
  type RedoBadgeColor,
} from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import type { ColumnDef } from "@tanstack/react-table";
import { memo } from "react";
import { useColumnSort } from "../../filters/filters-context";
import { ConversationActionsMenu } from "../components/editable-cells/conversation-actions-menu";
import { EditableAssigneeCell } from "../components/editable-cells/editable-assignee-cell";
import type { ConversationColumnMeta } from "../types";

function getPriorityConfig(priority: ConversationPriority): {
  label: string;
  color: RedoBadgeColor;
} {
  switch (priority) {
    case "CRITICAL":
      return { label: "Critical", color: "error" };
    case "HIGH":
      return { label: "High", color: "orange" };
    case "MEDIUM":
      return { label: "Medium", color: "warning" };
    case "LOW":
      return { label: "Low", color: "gray" };
    default:
      assertNever(priority);
  }
}

function getStatusConfig(status: ConversationStatus): {
  label: string;
  color: RedoBadgeColor;
} {
  switch (status) {
    case "OPEN":
      return { label: "Open", color: "success" };
    case "CLOSED":
      return { label: "Closed", color: "gray" };
    case "SNOOZED":
      return { label: "Snoozed", color: "warning" };
    case "IN_PROGRESS":
      return { label: "In Progress", color: "blue" };
    case "HIDDEN":
      return { label: "Hidden", color: "gray" };
    case "ARCHIVED":
      return { label: "Archived", color: "gray" };
    case "DELETED":
      return { label: "Deleted", color: "error" };
    default:
      assertNever(status);
  }
}

const SortableColumnHeader = memo(function SortableColumnHeader({
  sortKind,
  title,
}: {
  sortKind: ConversationSort["kind"];
  title: string;
}) {
  const { sortDirection, toggleSort } = useColumnSort(sortKind);

  return (
    <Flex align="center" cursor="pointer" gap="xs" onClick={toggleSort}>
      <Text fontSize="xs" fontWeight="semibold" textColor="tertiary">
        {title}
      </Text>
      {sortDirection !== null && (
        <Flex align="center" className="h-4 w-4">
          {sortDirection === "desc" ? <ArrowDown /> : <ArrowUp />}
        </Flex>
      )}
    </Flex>
  );
});

const ColumnHeader = memo(function ColumnHeader({ title }: { title: string }) {
  return (
    <Text fontSize="xs" fontWeight="semibold" textColor="tertiary">
      {title}
    </Text>
  );
});

function formatDateWithTime(date: Date): { date: string; time: string } {
  const dateStr = date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
  const timeStr = date
    .toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .toUpperCase();
  return { date: dateStr, time: timeStr };
}

/**
 * Get column definitions for conversations table
 * Column order:
 * 1. Read indicator (black dot if unread)
 * 2. Ticket #
 * 3. Priority
 * 4. Status
 * 5. Customer
 * 6. Summary
 * 7. Product
 * 8. Assignee
 * 9. Last response
 * 10. Created at
 * 11. Actions (3-dot menu)
 */
export function getConversationsTableColumns(_options?: {
  enablePinning?: boolean;
}): ColumnDef<ConversationListItemResponse, unknown>[] {
  return [
    {
      id: "read",
      accessorKey: "readAt",
      header: () => null,
      cell: ({ row }) => {
        const readAt = row.original.readAt;
        if (!readAt) {
          return (
            <Flex align="center" justify="center">
              <div
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: "black",
                }}
              />
            </Flex>
          );
        }
        return null;
      },
      meta: { label: "Read", variant: "text" } as ConversationColumnMeta,
      enableSorting: false,
      enableColumnFilter: false,
      enableHiding: false,
      size: 24,
    },
    {
      id: "ticketNumber",
      accessorKey: "id",
      header: () => <ColumnHeader title="Ticket #" />,
      cell: ({ row }) => {
        const id = row.original.id;
        // Display shortened ticket ID (first 8 chars or use a ticket number if available)
        const ticketDisplay = `#${id.slice(0, 8)}`;
        return (
          <Text fontSize="xs" textColor="tertiary">
            {ticketDisplay}
          </Text>
        );
      },
      meta: { label: "Ticket #", variant: "text" } as ConversationColumnMeta,
      enableSorting: false,
      enableColumnFilter: false,
      size: 80,
    },
    {
      id: "priority",
      accessorKey: "priority",
      header: () => <ColumnHeader title="Priority" />,
      cell: ({ row }) => {
        const priority = row.original.priority;
        if (!priority) {
          return (
            <Text fontSize="xs" textColor="tertiary">
              -
            </Text>
          );
        }
        const config = getPriorityConfig(priority);
        return <RedoBadge color={config.color} size="sm" text={config.label} />;
      },
      meta: { label: "Priority", variant: "badge" } as ConversationColumnMeta,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: (row, id, value: string[]) => {
        const cellValue = row.getValue(id);
        if (!cellValue) return false;
        return value.includes(cellValue as string);
      },
      size: 120,
    },
    {
      id: "status",
      accessorKey: "status",
      header: () => <ColumnHeader title="Status" />,
      cell: ({ row }) => {
        const config = getStatusConfig(row.original.status);
        return <RedoBadge color={config.color} size="sm" text={config.label} />;
      },
      meta: { label: "Status", variant: "badge" } as ConversationColumnMeta,
      enableSorting: false,
      enableColumnFilter: true,
      filterFn: (row, id, value: string[]) => {
        const cellValue = row.getValue(id);
        return value.includes(cellValue as string);
      },
      size: 140,
    },
    {
      id: "customer",
      accessorKey: "mongoCustomerId",
      header: () => <ColumnHeader title="Customer" />,
      cell: ({ row }) => {
        const customer = row.original.customer;
        return (
          <Flex dir="column" gap="none">
            <Text fontSize="xs" fontWeight="medium" textColor="primary">
              {customer?.displayName ?? "Unknown Customer"}
            </Text>
            {customer?.email && (
              <Text fontSize="xxs" textColor="tertiary">
                {customer.email}
              </Text>
            )}
          </Flex>
        );
      },
      meta: { label: "Customer", variant: "text" } as ConversationColumnMeta,
      enableSorting: false,
      enableColumnFilter: false,
      size: 160,
    },
    {
      id: "summary",
      accessorKey: "summary",
      header: () => <ColumnHeader title="Summary" />,
      cell: ({ row }) => {
        const summary = row.original.summary;
        return (
          <Text
            fontSize="xs"
            overflow="hidden"
            textColor="tertiary"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {summary ?? "No summary"}
          </Text>
        );
      },
      meta: { label: "Summary", variant: "text" } as ConversationColumnMeta,
      enableSorting: false,
      enableColumnFilter: false,
      size: 200,
      minSize: 200,
    },
    {
      id: "product",
      header: () => <ColumnHeader title="Product" />,
      cell: () => {
        // TODO: Add product data to conversation response
        return (
          <Text fontSize="xs" textColor="tertiary">
            -
          </Text>
        );
      },
      meta: { label: "Product", variant: "text" } as ConversationColumnMeta,
      enableSorting: false,
      enableColumnFilter: false,
      size: 160,
    },
    {
      id: "assignee",
      header: () => <ColumnHeader title="Assignee" />,
      cell: ({ row }) => {
        return <EditableAssigneeCell conversation={row.original} />;
      },
      meta: { label: "Assignee", variant: "text" } as ConversationColumnMeta,
      enableSorting: false,
      enableColumnFilter: false,
      size: 100,
    },
    {
      id: "lastResponse",
      accessorKey: "lastCustomerMessageAt",
      header: () => (
        <SortableColumnHeader
          sortKind="lastCustomerMessageAt"
          title="Last response"
        />
      ),
      cell: ({ row }) => {
        const date = row.original.lastCustomerMessageAt;
        if (!date) {
          return (
            <Text fontSize="xs" textColor="tertiary">
              -
            </Text>
          );
        }
        const formatted = formatDateWithTime(date);
        return (
          <Flex dir="column" gap="none">
            <Text fontSize="xs" fontWeight="medium" textColor="tertiary">
              {formatted.date}
            </Text>
            <Text fontSize="xs" textColor="tertiary">
              {formatted.time}
            </Text>
          </Flex>
        );
      },
      meta: {
        label: "Last response",
        variant: "date",
      } as ConversationColumnMeta,
      enableSorting: true,
      enableColumnFilter: false,
      size: 130,
    },
    {
      id: "createdAt",
      header: () => (
        <SortableColumnHeader sortKind="createdAt" title="Created at" />
      ),
      cell: ({ row }) => {
        // Use lastCustomerMessageAt as a proxy for createdAt until we have actual createdAt
        const date = row.original.lastCustomerMessageAt;
        if (!date) {
          return (
            <Text fontSize="xs" textColor="tertiary">
              -
            </Text>
          );
        }
        const formatted = formatDateWithTime(date);
        return (
          <Flex dir="column" gap="none">
            <Text fontSize="xs" fontWeight="medium" textColor="tertiary">
              {formatted.date}
            </Text>
            <Text fontSize="xs" textColor="tertiary">
              {formatted.time}
            </Text>
          </Flex>
        );
      },
      meta: { label: "Created at", variant: "date" } as ConversationColumnMeta,
      enableSorting: true,
      enableColumnFilter: false,
      size: 130,
    },
    {
      id: "actions",
      header: () => null,
      cell: ({ row }) => (
        <ConversationActionsMenu conversation={row.original} />
      ),
      meta: { label: "Actions", variant: "text" } as ConversationColumnMeta,
      enableSorting: false,
      enableColumnFilter: false,
      enableHiding: false,
      size: 48,
    },
  ];
}
